// src/services/diceMatchService.ts
import { logger } from '../utils/logger';

import { supabase } from './supabase';
import { GameResult } from '../components/games/diceMatch/types';

export interface DiceMatchScore {
  id: string;
  user_id: string;
  score: number;
  highest_die: string;
  sparkles_created: number;
  moves_used: number;
  created_at: string;
  // Joined from profiles
  username?: string;
  display_name?: string;
}

export interface MetaProgression {
  id: string;
  user_id: string;
  meta_currency: number;
  total_games_played: number;
  total_sparkles_created: number;
  total_score: number;
  best_score: number;
  upgrade_starting_moves: number;
  upgrade_starting_dice: number;
  upgrade_starting_grid: number;
  upgrade_currency_multiplier: number;
  created_at: string;
  updated_at: string;
}

/**
 * Submit a game score to the database
 */
export async function submitGameScore(result: GameResult): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      logger.error('No user logged in');
      return false;
    }

    const { error } = await supabase
      .from('dice_match_scores')
      .insert({
        user_id: user.id,
        score: result.score,
        highest_die: result.highestDie,
        sparkles_created: result.sparklesCreated,
        moves_used: result.movesUsed,
      });

    if (error) {
      logger.error('Error submitting score:', error);
      return false;
    }

    // Update meta progression
    await updateMetaProgression(result);

    return true;
  } catch (error) {
    logger.error('Error in submitGameScore:', error);
    return false;
  }
}

/**
 * Update meta progression after a game
 */
async function updateMetaProgression(result: GameResult): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Get or create meta progression
    const { data: meta, error: fetchError } = await supabase
      .from('dice_match_meta_progression')
      .select('*')
      .eq('user_id', user.id)
      .single();

    if (fetchError || !meta) {
      // Create new meta progression record
      const { data: newMeta, error: createError } = await supabase
        .from('dice_match_meta_progression')
        .insert({
          user_id: user.id,
          meta_currency: result.metaCurrencyEarned,
          total_games_played: 1,
          total_sparkles_created: result.sparklesCreated,
          total_score: result.score,
          best_score: result.score,
        })
        .select()
        .single();

      if (createError) {
        logger.error('Error creating meta progression:', createError);
      }
      return;
    }

    // Update existing meta progression
    const { error: updateError } = await supabase
      .from('dice_match_meta_progression')
      .update({
        meta_currency: meta.meta_currency + result.metaCurrencyEarned,
        total_games_played: meta.total_games_played + 1,
        total_sparkles_created: meta.total_sparkles_created + result.sparklesCreated,
        total_score: meta.total_score + result.score,
        best_score: Math.max(meta.best_score, result.score),
      })
      .eq('user_id', user.id);

    if (updateError) {
      logger.error('Error updating meta progression:', updateError);
    }
  } catch (error) {
    logger.error('Error in updateMetaProgression:', error);
  }
}

/**
 * Get top scores for leaderboard
 */
export async function getLeaderboard(limit: number = 10): Promise<DiceMatchScore[]> {
  try {
    logger.log('🔍 Fetching leaderboard with limit:', limit);

    // Fetch more scores than needed to ensure we get enough unique users
    // (user_id references auth.users, not profiles)
    const { data: scores, error: scoresError } = await supabase
      .from('dice_match_scores')
      .select('id, user_id, score, highest_die, sparkles_created, moves_used, created_at')
      .order('score', { ascending: false })
      .limit(limit * 3); // Fetch 3x to account for duplicate users

    if (scoresError) {
      logger.error('❌ Error fetching leaderboard scores:', scoresError);
      logger.error('Error code:', scoresError.code);
      logger.error('Error message:', scoresError.message);
      return [];
    }

    logger.log('✅ Leaderboard scores query successful');
    logger.log('📈 Number of scores:', scores?.length ?? 0);

    if (!scores || scores.length === 0) {
      logger.warn('⚠️ No leaderboard data returned from query');
      return [];
    }

    // Deduplicate: keep only the highest score per user
    const seenUsers = new Set<string>();
    const uniqueScores = scores.filter(score => {
      if (seenUsers.has(score.user_id)) {
        return false;
      }
      seenUsers.add(score.user_id);
      return true;
    }).slice(0, limit); // Take only the top N unique users

    logger.log('📊 Unique users after deduplication:', uniqueScores.length);

    // Fetch profiles for all unique user IDs (profiles.id = auth.users.id)
    const userIds = uniqueScores.map(s => s.user_id);
    const { data: profiles, error: profilesError } = await supabase
      .from('profiles')
      .select('id, username, display_name')
      .in('id', userIds);

    if (profilesError) {
      logger.warn('⚠️ Error fetching profiles for leaderboard:', profilesError);
      // Continue without profile data rather than failing completely
    }

    logger.log('✅ Profiles fetched:', profiles?.length ?? 0);

    // Create a map of user_id -> profile for quick lookup
    const profileMap = new Map(profiles?.map(p => [p.id, p]) ?? []);

    // Combine scores with profile data
    return uniqueScores.map(score => {
      const profile = profileMap.get(score.user_id);
      return {
        id: score.id,
        user_id: score.user_id,
        score: score.score,
        highest_die: score.highest_die,
        sparkles_created: score.sparkles_created,
        moves_used: score.moves_used,
        created_at: score.created_at,
        username: profile?.username,
        display_name: profile?.display_name,
      };
    });
  } catch (error) {
    logger.error('❌ Error in getLeaderboard:', error);
    return [];
  }
}

/**
 * Get user's personal best score
 */
export async function getPersonalBest(): Promise<number> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return 0;

    const { data, error } = await supabase
      .from('dice_match_scores')
      .select('score')
      .eq('user_id', user.id)
      .order('score', { ascending: false })
      .limit(1)
      .single();

    if (error && error.code !== 'PGRST116') {
      logger.error('Error getting personal best:', error);
      return 0;
    }

    return data?.score ?? 0;
  } catch (error) {
    logger.error('Error in getPersonalBest:', error);
    return 0;
  }
}

/**
 * Get user's meta progression
 */
export async function getMetaProgression(): Promise<MetaProgression | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from('dice_match_meta_progression')
      .select('*')
      .eq('user_id', user.id)
      .single();

    if (error && error.code !== 'PGRST116') {
      logger.error('Error getting meta progression:', error);
      return null;
    }

    // Create if doesn't exist
    if (!data) {
      const { data: newData, error: createError } = await supabase
        .from('dice_match_meta_progression')
        .insert({
          user_id: user.id,
        })
        .select()
        .single();

      if (createError) {
        logger.error('Error creating meta progression:', createError);
        return null;
      }

      return newData;
    }

    return data;
  } catch (error) {
    logger.error('Error in getMetaProgression:', error);
    return null;
  }
}

/**
 * Purchase a meta upgrade
 */
export async function purchaseMetaUpgrade(
  upgradeType: 'starting_moves' | 'starting_dice' | 'starting_grid' | 'currency_multiplier',
  cost: number
): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const meta = await getMetaProgression();
    if (!meta || meta.meta_currency < cost) {
      return false;
    }

    const updates: any = {
      meta_currency: meta.meta_currency - cost,
    };

    const upgradeField = `upgrade_${upgradeType}`;
    updates[upgradeField] = (meta as any)[upgradeField] + 1;

    const { error } = await supabase
      .from('dice_match_meta_progression')
      .update(updates)
      .eq('user_id', user.id);

    if (error) {
      logger.error('Error purchasing upgrade:', error);
      return false;
    }

    return true;
  } catch (error) {
    logger.error('Error in purchaseMetaUpgrade:', error);
    return false;
  }
}

/**
 * Get user's recent scores
 */
export async function getRecentScores(limit: number = 5): Promise<DiceMatchScore[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('dice_match_scores')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      logger.error('Error getting recent scores:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    logger.error('Error in getRecentScores:', error);
    return [];
  }
}
